import DragIndicatorIcon from "@mui/icons-material/DragIndicatorRounded";
import MoreHorizIcon from "@mui/icons-material/MoreHorizRounded";
import type { SxProps, Theme } from "@mui/material";
import { Divider, Grid, IconButton, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import type { AccessPermission } from "@entrilia/shared/api/types";
import AuthorizedBox from "@entrilia/shared/components/AuthorizedBox";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill";
import { useClientPermissionsContext } from "@entrilia/shared/contexts/ClientPermissionsContext";

import type { ReportGroup } from "../../../../../api/types/svTypes.ts";

interface Props {
  level: ReportGroup;
  sx?: SxProps<Theme>;
  clone?: boolean;
  onDragMove?: (group: ReportGroup, replaceGroup: ReportGroup) => void;
  onEndMoveItem?: (group: ReportGroup) => void;
  onShowMenu?: (anchorEL: HTMLElement | null, groupId: string) => void;
  permissions?: AccessPermission[];
}

export const GroupItem = (props: Props) => {
  const { level, sx, clone, onDragMove, onEndMoveItem, onShowMenu, permissions } = props;

  const { hasAnyPermission } = useClientPermissionsContext();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [focused, setFocused] = useState(false);

  const allowDradAndDrop = hasAnyPermission([...(permissions ?? [])]);

  const [{ isDragging }, drag, dragPreview] = useDrag(() => ({
    type: "level",
    item: level,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (item, monitor) => {
      if (monitor.didDrop()) {
        onEndMoveItem?.call(this, item);
      }
    },
  }));

  const [, drop] = useDrop(
    () => ({
      accept: ["level"],
      hover(item: ReportGroup) {
        if (item.id !== level.id) {
          onDragMove?.call(this, item, level);
        }
      },
    }),
    []
  );

  useEffect(() => {
    dragPreview(getEmptyImage());
  }, [dragPreview]);

  return (
    <>
      <Grid
        role="Handle"
        ref={(node) => {
          if (allowDradAndDrop) drag(drop(node));
        }}
        container
        onMouseEnter={() => setFocused(true)}
        onMouseLeave={() => setFocused(false)}
        sx={(theme) => ({
          height: "36px",
          alignItems: "center",
          gap: 2,
          px: 1,
          backgroundColor: focused || clone ? theme.palette.background.grey : theme.palette.background.paper,
          opacity: isDragging ? 0 : 1,
          borderTop: clone ? `1px solid ${theme.palette.divider}` : "inherit",
          borderBottom: clone ? `1px solid ${theme.palette.divider}` : "inherit",
          width: "100%",
          ":hover": { cursor: "pointer" },
          ...sx,
        })}
      >
        <Grid>
          <DragIndicatorIcon
            sx={(theme) => ({
              visibility: focused || clone ? "visible" : "hidden",
              color: theme.palette.secondary.light,
            })}
          />
        </Grid>
        <Typography>{level.caption}</Typography>
        <HorizontalFill />
        <AuthorizedBox permissions={[...(permissions || [])]}>
          <IconButton
            ref={(r) => setAnchorEl(r)}
            sx={{
              visibility: focused || clone ? "visible" : "hidden",
              ":hover": { cursor: "pointer" },
            }}
            onClick={() => onShowMenu?.apply(null, [anchorEl, level.id])}
          >
            <MoreHorizIcon />
          </IconButton>
        </AuthorizedBox>
      </Grid>
      <Divider flexItem orientation="horizontal" />
    </>
  );
};

export default GroupItem;
